import React from 'react';
import './ProgramsPage.css';

// Import images
import teacherImage from '../assets/images/teacher-4784916_1920.jpg';
import studentsImage from '../assets/images/students-99506_1920.jpg';
import childrenLearning from '../assets/images/children-593313_1920.jpg';

const ProgramsPage = ({ onNavigate }) => {
  return (
    <div className="programs-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="logo-container">
                <img 
                  src="https://static.wixstatic.com/media/1d90b3_588e219d6cc2449d84256529ea18235d~mv2.png" 
                  alt="Digital Flourishing for Young Minds" 
                  className="page-logo"
                />
              </div>
              <h1>Transform Your School's Digital Culture Today</h1>
              <p className="hero-description">
                Evidence-based programs designed to tackle the real challenges schools face: 
                declining attention, digital distraction, and student anxiety. We provide proven solutions 
                that work in your school context.
              </p>
              <button className="btn btn-primary" onClick={() => onNavigate('contact')}>
                Book a Discovery Call
              </button>
            </div>
            <div className="hero-image">
              <img src={teacherImage} alt="Teacher engaging with students in classroom" />
            </div>
          </div>
        </div>
      </section>

      {/* The Challenge Section */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header text-center">
            <h2>The Digital Challenge Facing UK Schools</h2>
            <p>Schools today face unprecedented challenges with student digital wellness</p>
          </div>

          <div className="challenges-grid">
            <div className="challenge-item">
              <div className="challenge-number">82%</div>
              <div className="challenge-text">
                <strong>Digital Distraction</strong>
                <p>of UK schools report digital distraction as a major classroom challenge</p>
              </div>
            </div>
            <div className="challenge-item">
              <div className="challenge-number">15%</div>
              <div className="challenge-text">
                <strong>Lost Instructional Time</strong>
                <p>Teachers lose 15% of instructional time to technology disruptions</p>
              </div>
            </div>
            <div className="challenge-item">
              <div className="challenge-number">68%</div>
              <div className="challenge-text">
                <strong>Student Wellbeing</strong>
                <p>of UK teenagers report sleep issues linked to screen time</p>
              </div>
            </div>
            <div className="challenge-item">
              <div className="challenge-number">35%</div>
              <div className="challenge-text">
                <strong>Rising Anxiety</strong>
                <p>increase in anxiety among UK young people correlates with social media use</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Solution Section */}
      <section className="section section-white">
        <div className="container">
          <div className="section-header text-center">
            <h2>Our Proven Solution</h2>
            <p>
              We address these challenges head-on with comprehensive, evidence-based programs 
              that equip educators and parents with practical tools and strategies.
            </p>
          </div>

          <div className="programs-grid">
            {/* Teacher Program */}
            <div className="program-card">
              <div className="program-image">
                <img src={studentsImage} alt="Students learning with technology" />
              </div>
              <div className="program-content">
                <h3>Teacher Professional Development Program</h3>
                
                <div className="program-problem">
                  <h4>The Problem Teachers Face</h4>
                  <p>
                    Teachers struggle to manage digital distractions in the classroom while maintaining 
                    engagement and supporting student wellbeing. Traditional approaches don't address the 
                    root causes of digital overwhelm.
                  </p>
                </div>

                <div className="program-solution">
                  <h4>Our Solution</h4>
                  <p>
                    A 6-session comprehensive training program that equips teaching staff with evidence-based 
                    strategies for creating healthy digital boundaries and fostering intentional technology use.
                  </p>
                </div>

                <div className="program-details">
                  <div className="detail-item">
                    <strong>Duration:</strong> 6-session comprehensive training
                  </div>
                  <div className="detail-item">
                    <strong>Format:</strong> In-person workshops with ongoing support
                  </div>
                  <div className="detail-item">
                    <strong>Participants:</strong> Teaching staff and educational leaders
                  </div>
                </div>
                
                <div className="program-features">
                  <h4>What You'll Learn:</h4>
                  <ul>
                    <li>Understanding digital wellness and its impact on learning</li>
                    <li>Implementing the four-pillar Digital Flourishing framework</li>
                    <li>Practical classroom management strategies for technology</li>
                    <li>Creating healthy digital boundaries in educational settings</li>
                    <li>Supporting students with digital wellness challenges</li>
                    <li>Measuring and tracking digital wellness outcomes</li>
                  </ul>
                </div>

                <div className="program-outcomes">
                  <h4>Measurable Outcomes:</h4>
                  <div className="outcomes-grid">
                    <div className="outcome-item">
                      <div className="outcome-number">87%</div>
                      <div className="outcome-label">Improvement in Student Focus</div>
                    </div>
                    <div className="outcome-item">
                      <div className="outcome-number">20%</div>
                      <div className="outcome-label">Reduction in Digital Disruptions</div>
                    </div>
                    <div className="outcome-item">
                      <div className="outcome-number">95%</div>
                      <div className="outcome-label">Teacher Satisfaction Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Parent Program */}
            <div className="program-card">
              <div className="program-image">
                <img src={childrenLearning} alt="Children engaged in collaborative learning" />
              </div>
              <div className="program-content">
                <h3>Parent Workshop Series</h3>
                
                <div className="program-problem">
                  <h4>The Problem Parents Face</h4>
                  <p>
                    Parents feel overwhelmed by screen time battles, unsure how to set healthy boundaries, 
                    and worried about their child's digital wellbeing. They need practical, evidence-based 
                    guidance they can implement immediately.
                  </p>
                </div>

                <div className="program-solution">
                  <h4>Our Solution</h4>
                  <p>
                    A 4-session workshop series that empowers parents with practical tools, conversation 
                    starters, and family-centered strategies for building healthy digital habits at home.
                  </p>
                </div>

                <div className="program-details">
                  <div className="detail-item">
                    <strong>Duration:</strong> 4-session workshop series
                  </div>
                  <div className="detail-item">
                    <strong>Format:</strong> Evening sessions with take-home resources
                  </div>
                  <div className="detail-item">
                    <strong>Participants:</strong> Parents, caregivers, and families
                  </div>
                </div>
                
                <div className="program-features">
                  <h4>What You'll Learn:</h4>
                  <ul>
                    <li>Creating healthy digital habits at home</li>
                    <li>Understanding age-appropriate screen time guidelines</li>
                    <li>Managing family technology boundaries without conflict</li>
                    <li>Supporting your child's digital wellness journey</li>
                    <li>Recognizing signs of digital overwhelm</li>
                    <li>Building positive family technology practices</li>
                  </ul>
                </div>

                <div className="program-outcomes">
                  <h4>Family Benefits:</h4>
                  <div className="outcomes-grid">
                    <div className="outcome-item">
                      <div className="outcome-number">78%</div>
                      <div className="outcome-label">Improved Family Communication</div>
                    </div>
                    <div className="outcome-item">
                      <div className="outcome-number">65%</div>
                      <div className="outcome-label">Better Sleep Quality</div>
                    </div>
                    <div className="outcome-item">
                      <div className="outcome-number">82%</div>
                      <div className="outcome-label">Reduced Screen Time Conflicts</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header text-center">
            <h2>Our Implementation Process</h2>
            <p>A structured approach to transforming your school's digital culture</p>
          </div>

          <div className="process-steps">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Initial Consultation</h3>
                <p>We assess your school's current digital culture and identify specific needs and challenges.</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Customized Program Design</h3>
                <p>We tailor our programs to address your school's unique context and requirements.</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Professional Development Delivery</h3>
                <p>Our expert facilitators deliver engaging, practical training sessions for your staff.</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Parent Workshop Implementation</h3>
                <p>We engage families through our comprehensive parent workshop series.</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Ongoing Support & Evaluation</h3>
                <p>We provide continued support and measure outcomes to ensure lasting impact.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Prospectus Section */}
      <section className="section section-white">
        <div className="container">
          <div className="prospectus-container">
            <div className="prospectus-content">
              <h2>Ready to Learn More?</h2>
              <p>
                Download our comprehensive School Prospectus to see detailed program modules, 
                implementation timelines, pricing, and case studies from schools that have successfully 
                transformed their digital culture.
              </p>
              <button className="btn btn-primary btn-large" onClick={() => onNavigate('contact')}>
                📧 Request School Prospectus
              </button>
              <p className="prospectus-note">
                Send us an email • We'll respond within 24 hours
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section section-teal">
        <div className="container text-center">
          <h2 style={{color: 'white', marginBottom: '1rem'}}>Ready to Transform Your School?</h2>
          <p style={{color: 'white', fontSize: '1.2rem', marginBottom: '2rem'}}>
            Schedule a free discovery call with our team to discuss how we can customize our programs 
            for your educational institution.
          </p>
          <button className="btn btn-secondary" onClick={() => onNavigate('contact')}>
            Book Your Discovery Call
          </button>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
